import { MouseEvent, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  OutlinedInput,
  Paper,
  Popper,
  Stack,
  Typography as T,
} from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import { addDatasetTables } from "datasets/pages/tables/api/modifyDatasetTables";
import TableTreeView from "datasets/pages/tables/components/TableTreeView";
import useTablesHierarchy from "datasets/pages/tables/hooks/useTablesHierarchy";
import pluralize from "pluralize";
import { TrashIcon } from "~/assets/icons";
import useErrorToast from "~/contexts/error-toast";
import { TableAccessLevel } from "~/pages/datasets/types/enums/access-level";
import rumClient, { trackEvent } from "~/utils/monitoring";

const SnowflakeForm = ({ accessLevel }: { accessLevel?: TableAccessLevel }) => {
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const { showError } = useErrorToast();
  const { mutate: mutateDataset } = useDataset();
  const { mutate: mutateFQNs } = useDatasetTableFQNs();
  const inputRef = useRef<HTMLInputElement>(null);
  const [tableSearch, setTableSearch] = useState("");
  const [tentativeSelections, setTentativeSelections] = useState<string[]>([]);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [tablesMenuAnchorEl, setTablesMenuAnchorEl] = useState<null | HTMLElement>(null);
  // Pre-load tables
  const { data: tableTree } = useTablesHierarchy(tableSearch, accessLevel);
  const allTablesSelected = Boolean(tableTree && !tableTree.length);

  const handleTableMenuOpen = (event: MouseEvent<HTMLInputElement>) => {
    setTablesMenuAnchorEl(event.currentTarget);
  };

  const handleTableMenuClose = () => {
    setTablesMenuAnchorEl(null);
    // Reset form to current selections
    setTentativeSelections(selectedTables);
  };

  const handleSelectTables = () => {
    setTablesMenuAnchorEl(null);
    // Update current selections
    setSelectedTables(tentativeSelections);
  };

  const onSubmit = async () => {
    if (!datasetId) {
      return;
    }

    setSubmitting(true);

    try {
      const { added, failed } = await addDatasetTables(datasetId, selectedTables);

      trackEvent("dataset_tables_added", {
        dataset_id: datasetId,
        count: added.length,
        ui: "classic",
      });

      mutateDataset();
      await mutateFQNs();

      if (failed.length) {
        showError(
          `Some tables could not be added: ${failed
            .map((table) => `${table.table_fqn} (${table.reason})`)
            .join("; ")}`
        );
      }

      navigate(`/data-browser/datasets/${datasetId}/tables`);
    } catch (e) {
      rumClient?.recordError(e);
      showError(e instanceof Error ? e.message : "Unable to add tables to dataset");
    }

    setSubmitting(false);
  };

  return (
    <Stack gap={3} alignItems="flex-start">
      <Stack maxWidth={600} width="100%" gap={1}>
        <Stack gap={0.5} width="100%">
          <T variant="body2" fontWeight={500}>
            Tables
          </T>
          <ClickAwayListener onClickAway={handleTableMenuClose}>
            <div>
              <OutlinedInput
                ref={inputRef}
                fullWidth
                placeholder="Select tables and views..."
                startAdornment={<Search fontSize="small" />}
                size="small"
                sx={{ input: { pl: 0.5 } }}
                onClick={handleTableMenuOpen}
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
              />
              <Popper
                anchorEl={tablesMenuAnchorEl}
                open={!!tablesMenuAnchorEl}
                placement="bottom-start"
              >
                <Paper
                  sx={{
                    mt: 0.5,
                    boxShadow:
                      "0px 1px 5px 0px #0000001F, 0px 2px 2px 0px #00000024, 0px 3px 1px -2px #00000033",
                  }}
                >
                  <Stack alignItems="flex-end" p={1}>
                    <Box width={660} height={400} overflow="auto">
                      <TableTreeView
                        searchTerm={tableSearch}
                        selectedItems={tentativeSelections}
                        setSelectedItems={setTentativeSelections}
                        accessLevel={accessLevel}
                      />
                    </Box>
                    <Box display="flex" gap={1} p={1}>
                      <Button variant="outlined" onClick={handleTableMenuClose}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        disabled={!tentativeSelections.length || allTablesSelected}
                        onClick={handleSelectTables}
                      >
                        Select
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              </Popper>
            </div>
          </ClickAwayListener>
        </Stack>
        <Stack gap={0.75}>
          {selectedTables.map((table, i) => (
            <Box
              key={i}
              sx={{
                alignItems: "center",
                bgcolor: "grey.100",
                borderRadius: 1.5,
                display: "flex",
                justifyContent: "space-between",
                px: 2,
                py: 1,
              }}
            >
              <T variant="body2">{table.replace(/^snowflake./, "")}</T>
              <IconButton
                onClick={() => setSelectedTables((prev) => prev.filter((fqn) => fqn !== table))}
              >
                <TrashIcon />
              </IconButton>
            </Box>
          ))}
        </Stack>
      </Stack>
      <LoadingButton
        loading={submitting}
        disabled={!selectedTables.length}
        type="submit"
        variant="contained"
        onClick={onSubmit}
      >
        Add {pluralize("Table", tentativeSelections.length)}
      </LoadingButton>
    </Stack>
  );
};

export default SnowflakeForm;
